import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";
import toast from "react-hot-toast";
import { AuthAPI } from "../api/AuthAPI";
import { useDebounce } from "../hooks/useDebounce";

import LazyImage from "../components/LazyImage";
import SidebarContent from "../components/SidebarContent";
import RichText from "../components/RichText";

export default function Products() {
  const [params, setParams] = useSearchParams();
  const { addToCart } = useCart();
  /* ================= STATES ================= */

  const [search, setSearch] = useState(params.get("search") || "");

  const [categoryId, setCategoryId] = useState(params.get("category_id") || "");

  const [price, setPrice] = useState(Number(params.get("max_price")) || 1000);

  const [appliedPrice, setAppliedPrice] = useState(
    Number(params.get("max_price")) || 1000,
  );

  const [limit] = useState(Number(params.get("limit")) || 12);

  const [page, setPage] = useState(Number(params.get("page")) || 1);

  const [sort, setSort] = useState(params.get("sort") || "old");

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const [products, setProducts] = useState([]);

  const [categories, setCategories] = useState([]);

  const [loading, setLoading] = useState(false);

  const [pagination, setPagination] = useState(null);

  const [expandedId, setExpandedId] = useState(null);

  const debouncedSearch = useDebounce(search, 500);

  /* ================= LOAD CATEGORIES ================= */

  useEffect(() => {
    let ignore = false;

    async function loadCategories() {
      try {
        const res = await AuthAPI.getcategories({
          limit: 50,
        });

        if (!ignore) {
          setCategories(res?.data?.data || []);
        }
      } catch (error) {
        console.error("Category API Error", error);
      }
    }

    loadCategories();

    return () => {
      ignore = true;
    };
  }, []);

  /* ================= PRODUCTS API ================= */

  useEffect(() => {
    async function loadProducts() {
      try {
        setLoading(true);

        const query = {
          page: String(page),
          limit: String(limit),
          max_price: String(appliedPrice),
          sort,
        };

        if (debouncedSearch) {
          query.search = debouncedSearch;
        }

        if (categoryId) {
          query.category_id = categoryId;
        }

        const currentParams = Object.fromEntries([...params]);

        const isSameParams =
          JSON.stringify(currentParams) === JSON.stringify(query);

        if (!isSameParams) {
          setParams(query, {
            replace: true,
          });
        }

        const payload = {
          search: debouncedSearch || undefined,
          page,
          limit,
          category_id: categoryId || undefined,
          min_price: 0,
          max_price: appliedPrice,
          sort,
        };

        const res = await AuthAPI.getproducts(payload);

        setProducts(res?.data?.data || []);

        setPagination(res?.data?.settings || null);
      } catch (error) {
        console.error("Products API Error", error);
      } finally {
        setLoading(false);
      }
    }

    loadProducts();
  }, [debouncedSearch, appliedPrice, page, limit, categoryId, sort]);

  /* ================= CLEAR FILTERS ================= */

  const clearFilters = () => {
    setSearch("");
    setCategoryId("");
    setPrice(1000);
    setAppliedPrice(1000);
    setPage(1);
    setSort("old");
  };

  return (
    <section
      className="
        min-h-screen
        bg-gradient-to-b
        from-[#fff7fb]
        via-white
        to-[#f5f7ff]
        font-poppins
      "
    >
      {/* HERO */}
      <section
        className="
          relative
          overflow-hidden
          py-10
          lg:py-14
          bg-gradient-to-r
          from-[#1A237E]
          via-[#283593]
          to-[#EC4899]
        "
      >
        <div className="absolute top-0 left-0 w-96 h-96 bg-pink-300/20 blur-3xl rounded-full"></div>

        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-300/20 blur-3xl rounded-full"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center text-white">
          <p className="uppercase tracking-[5px] text-pink-200 text-sm font-medium">
            Premium Collection
          </p>

          <h1
            className="
              mt-5
              font-playfair
              text-[32px]
              md:text-[56px]
              leading-[1]
              font-bold
            "
          >
            Our Products
          </h1>

          <p
            className="
              mt-6
              max-w-2xl
              mx-auto
              text-white/80
              text-lg
              leading-relaxed
            "
          >
            Discover premium sanitary and wellness essentials crafted with
            comfort, confidence and care.
          </p>
        </div>
      </section>

      {/* MAIN */}
      <div className="max-w-[1550px] mx-auto px-4 py-8 lg:py-14">
        <div className="flex gap-10">
          {/* SIDEBAR */}
          <aside className="hidden lg:block w-[310px] shrink-0">
            <div
              className="
                sticky
                top-[100px]

                rounded-[32px]
                bg-white/80
                backdrop-blur-xl
                border
                border-pink-100
                shadow-[0_10px_40px_rgba(255,192,203,0.12)]

                p-6
              "
            >
              <SidebarContent
                search={search}
                setSearch={setSearch}
                categoryId={categoryId}
                setCategoryId={setCategoryId}
                categories={categories}
                price={price}
                setPrice={setPrice}
                setAppliedPrice={setAppliedPrice}
                setPage={setPage}
                clearFilters={clearFilters}
                setMobileFiltersOpen={setMobileFiltersOpen}
              />
            </div>
          </aside>

          {/* PRODUCTS */}
          <main className="flex-1 min-w-0">
            {/* TOP BAR */}
            <div className="flex items-center justify-between mb-10">
              {/* MOBILE FILTER */}
              <button
                onClick={() => setMobileFiltersOpen(true)}
                className="
                  lg:hidden

                  flex
                  items-center
                  gap-2

                  rounded-2xl
                  bg-gradient-to-r
                  from-pink-500
                  to-pink-400

                  px-3
                  py-3

                  text-white
                  font-medium
                  shadow-lg
                "
              >
                <SlidersHorizontal size={16} />
                Filters
              </button>

              {/* SORT */}
              <select
                value={sort}
                onChange={(e) => {
                  setSort(e.target.value);
                  setPage(1);
                }}
                className="
                  rounded-2xl
                  border border-pink-100
                  bg-white/90
                  backdrop-blur-md

                  px-5
                  py-3

                  text-gray-700
                  shadow-sm
                  outline-none
                "
              >
                <option value="old">Old</option>
                <option value="latest">Latest</option>

                <option value="price_asc">Price: Low to High</option>

                <option value="price_desc">Price: High to Low</option>
              </select>
            </div>

            {/* LOADING */}
            {loading && (
              <div className="py-28 text-center text-gray-500 text-lg">
                Loading products...
              </div>
            )}

            {/* GRID */}
            {!loading && (
              <div
                className="
    grid
    grid-cols-2
    md:grid-cols-2
    lg:grid-cols-3
    xl:grid-cols-4

    gap-3
    md:gap-5
    lg:gap-7
  "
              >
                {products.map((p) => (
                  <div
                    key={p.id}
                    className="
                      group
                      h-full
                    "
                  >
                    <div
                      className="
                        relative
                        h-full

                        rounded-[34px]

                        bg-white/80
                        backdrop-blur-xl

                        border
                        border-pink-100

                        shadow-[0_12px_40px_rgba(255,192,203,0.12)]

                        overflow-hidden

                        transition-all
                        duration-500

                        hover:-translate-y-2
                        hover:shadow-[0_20px_60px_rgba(255,105,180,0.18)]

                        flex
                        flex-col
                      "
                    >
                      {/* TOP GLOW */}
                      <div
                        className="
                          absolute
                          top-0
                          inset-x-0
                          h-32
                          bg-gradient-to-b
                          from-pink-50
                          to-transparent
                        "
                      />

                      {/* IMAGE */}
                      <Link
                        to={`/products/${p.id}`}
                        className="
                          relative
                          pt-10
                          px-6

                          flex
                          justify-center
                          items-center
                        "
                      >
                        <LazyImage
                          src={p.image}
                          alt={p.name}
                          className="
  h-36
  sm:h-40
  md:h-52
  lg:h-64

  w-full
  object-contain

  transition-transform
  duration-500

  group-hover:scale-105
"
                        />
                      </Link>

                      {/* CONTENT */}
                      <div
                        className="
  px-3
  sm:px-4
  lg:px-6

  pb-4
  lg:pb-7

  pt-2

  flex
  flex-col
  flex-1
"
                      >
                        {/* NAME */}
                        <Link to={`/products/${p.id}`}>
                          <h3
                            className="
  text-sm
  md:text-base
  lg:text-[19px]

  font-semibold
  text-[#1A237E]
  leading-snug

  min-h-[52px]
  lg:min-h-[60px]
"
                          >
                            {p.name}
                          </h3>
                        </Link>

                        {/* DESCRIPTION */}
                        {/* <p
                          className={`
                            mt-3
                            text-[14px]
                            leading-relaxed
                            text-gray-600

                            min-h-[70px]

                            ${expandedId === p.id ? "" : "line-clamp-3"}
                          `}
                        >
                          <RichText
                            html={p.description}
                            clamp={expandedId !== p.id}
                            className="min-h-[70px]"
                          />
                        </p> */}

                        {/* READ MORE */}
                        {/* {p.description?.length > 80 && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();

                              e.stopPropagation();

                              setExpandedId(expandedId === p.id ? null : p.id);
                            }}
                            className="
                              mt-2
                              text-sm
                              font-medium
                              text-pink-500
                              hover:text-pink-600
                              text-left
                            "
                          >
                            {expandedId === p.id ? "Read less" : "Read more"}
                          </button>
                        )} */}

                        {/* PUSH BOTTOM */}
                        <div className="mt-auto">
                          {/* PRICE */}
                          <div className="mt-6 flex items-center gap-3">
                            {p.price && (
                              <span
                                className="
                                text-xl md:text-2xl lg:text-3xl
                                  font-bold
                                  text-[#1A237E]
                                "
                              >
                                ₹{p.price}
                              </span>
                            )}

                            {p.normal_price && (
                              <span
                                className="
                                  text-base
                                  text-gray-400
                                  line-through
                                "
                              >
                                ₹{p.normal_price}
                              </span>
                            )}
                          </div>

                          {/* BUTTONS */}
                          {/* BUTTONS */}
                          <div className="mt-5">
                            {p.is_out_of_stock ? (
                              <button
                                disabled
                                className="
        w-full

        py-3

        rounded-xl

        bg-gray-100
        border
        border-gray-200

        text-gray-400
        font-semibold

        cursor-not-allowed
      "
                              >
                                Out of Stock
                              </button>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.preventDefault();

                                  addToCart(p);

                                  toast.success("Added to cart!", {
                                    duration: 2000,
                                  });
                                }}
                                className="
        w-full

        flex
        items-center
        justify-center
        gap-2

        py-3

        rounded-xl

        bg-gradient-to-r
        from-pink-500
        via-pink-500
        to-fuchsia-500

        text-white
        text-sm
        md:text-base
        font-semibold

        shadow-md

        transition-all
        duration-300

        hover:shadow-xl
        hover:-translate-y-0.5

        active:scale-[0.98]
      "
                              >
                                <ShoppingBag size={18} />
                                Add To Cart
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* EMPTY */}
            {!loading && products.length === 0 && (
              <div className="py-24 text-center text-lg text-gray-500">
                No products found
              </div>
            )}

            {/* PAGINATION */}
            {pagination && (
              <div className="mt-16 flex items-center justify-center gap-5">
                <button
                  disabled={!pagination.prev_page}
                  onClick={() => setPage(page - 1)}
                  className="
                    px-6
                    py-3

                    rounded-2xl

                    bg-white
                    border
                    border-pink-100

                    text-gray-700

                    hover:bg-pink-50

                    disabled:opacity-50
                  "
                >
                  Prev
                </button>

                <div
                  className="
                    px-6
                    py-3

                    rounded-2xl

                    bg-white/80
                    backdrop-blur-md

                    border
                    border-pink-100

                    text-gray-700
                    shadow-sm
                  "
                >
                  Page {pagination.page} of {pagination.total_pages}
                </div>

                <button
                  disabled={!pagination.next_page}
                  onClick={() => setPage(page + 1)}
                  className="
                    px-6
                    py-3

                    rounded-2xl

                    bg-white
                    border
                    border-pink-100

                    text-gray-700

                    hover:bg-pink-50

                    disabled:opacity-50
                  "
                >
                  Next
                </button>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* MOBILE FILTER DRAWER */}
      <div
        className={`
          fixed inset-0 z-50 bg-black/40 transition-opacity lg:hidden
          ${mobileFiltersOpen ? "visible opacity-100" : "invisible opacity-0"}
        `}
        onClick={() => setMobileFiltersOpen(false)}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className={`
            absolute left-0 top-0
            h-full w-[340px]

            overflow-y-auto

            bg-white
            p-6

            transition-transform duration-300

            ${mobileFiltersOpen ? "translate-x-0" : "-translate-x-full"}
          `}
        >
          <SidebarContent
            search={search}
            setSearch={setSearch}
            categoryId={categoryId}
            setCategoryId={setCategoryId}
            categories={categories}
            price={price}
            setPrice={setPrice}
            setAppliedPrice={setAppliedPrice}
            setPage={setPage}
            clearFilters={clearFilters}
            setMobileFiltersOpen={setMobileFiltersOpen}
          />
        </div>
      </div>
    </section>
  );
}
